import { useCallback, useState, useEffect } from 'react';
import { rollD20, rollD6 } from '../utils/dice.js';
import { getEvent } from '../utils/events.js';
import { getStoryEvent } from '../config/storyEvents.js';
import { applyEventEffect, checkBirth } from '../systems/eventSystem.js';
import { rollAllTasks, pushTask } from '../systems/taskSystem.js';
import { injureWorker, addRefugees, createWorker, createImmatureWorker } from '../systems/workerSystem.js';
import { startBuilding } from '../systems/buildingSystem.js';
import { BUILDINGS } from '../config/buildings.js';
import { CONFIG } from '../config/constants.js';
import { checkVictory, checkDefeat } from '../systems/victorySystem.js';
import { useEndDay } from './useEndDay.js';
import { useAudio } from './useAudio.js';
import { executeTrade } from '../systems/tradeSystem.js';
import { hasSpecialMechanic } from '../systems/taskEffectsSystem.js';

/**
 * Manages phase transitions and provides handlers for each phase
 */
export function usePhaseManager(gameState, announce) {
  const { game, setGame, assignWorker, setBountyTask, reset } = gameState;
  const [showBuildSelect, setShowBuildSelect] = useState(false);
  const [showTradeDialog, setShowTradeDialog] = useState(false);
  const [tradeDialogData, setTradeDialogData] = useState(null);
  const audio = useAudio();

  const handleEndDay = useEndDay({ setGame });

  // Initialize event on mount and day start
  useEffect(() => {
    if (game.phase === 'event' && !game.currentEvent) {
      // If there's a pending victory claim, show it as this day's "event"
      if (game.pendingVictory) {
        const claimEvent = {
          type: 'victory_claim',
          name: `${game.pendingVictory.title} — Victory!`,
          icon: game.pendingVictory.icon,
          description: game.pendingVictory.message,
          choices: [
            {
              id: 'claim',
              label: 'Claim Victory!',
              description: 'End the game and celebrate your achievement',
              effects: { claimVictory: true }
            },
            {
              id: 'continue',
              label: 'Keep Playing',
              description: 'Press on toward an even greater destiny',
              effects: {}
            }
          ]
        };
        setGame(g => ({
          ...g,
          currentEvent: claimEvent,
          eventRoll: null,
          secondaryEvents: [],
          futureRefugeesArrivalNotice: null,
          diseaseNotice: null
        }));
        announce(`${game.pendingVictory.title}! Claim victory or keep playing?`);
        return;
      }

      // Check for story event first
      const storyEvent = getStoryEvent(game.day, game);

      let event, eventRoll;
      if (storyEvent) {
        event = storyEvent;
        eventRoll = null;
      } else {
        eventRoll = rollD20();
        event = getEvent(eventRoll);
        audio.playDice('d20', 1);
      }

      // Secondary events (births, refugees, disease)
      const secondaryEvents = [];

      const birthEvent = checkBirth(game);
      if (birthEvent) {
        secondaryEvents.push(birthEvent);
      }

      if (game.futureRefugeesArrivalNotice) {
        secondaryEvents.push(game.futureRefugeesArrivalNotice);
      }

      if (game.diseaseNotice) {
        secondaryEvents.push(game.diseaseNotice);
      }

      // Autumn warning: winter is coming (days 11-15 and 31-35)
      const upcomingWinter = (game.day >= 11 && game.day <= 15) || (game.day >= 31 && game.day <= 35);
      if (upcomingWinter && !storyEvent) {
        secondaryEvents.push({
          type: 'winter_warning',
          name: 'Winter Approaches',
          icon: '❄️',
          message: `Winter arrives on day ${game.day <= 15 ? 16 : 36} — farm output will drop by 1 per die.`
        });
      }

      setGame(g => ({
        ...g,
        eventRoll,
        currentEvent: event,
        secondaryEvents,
        tempWorker: null, // Traveler is now a choice event; temp worker is granted via handleStoryChoice
        futureRefugeesArrivalNotice: null,
        diseaseNotice: null
      }));

      const announcements = [
        `Day ${game.day}. Event: ${event.name}. ${event.description}`
      ];
      if (secondaryEvents.length > 0) {
        secondaryEvents.forEach(se => announcements.push(se.message));
      }
      announce(announcements.join(' '));
    }
  }, [game, setGame, announce]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize assignments
  useEffect(() => {
    if (game.phase === 'assign' && Object.keys(game.assignments).length === 0) {
      // Exclude immature children: they cannot work but still eat
      const aliveWorkers = game.workers.filter(w => w.status !== 'dead' && w.status !== 'immature');

      if (aliveWorkers.length === 0) {
        setGame(g => ({
          ...g,
          phase: 'gameover',
          gameOver: true,
          gameWon: false,
          ending: {
            type: 'defeat',
            icon: '💀',
            title: 'The Settlement Falls',
            message: `Your settlement fell on day ${g.day}. The wilderness claims another dream...`,
            color: 'text-red-700'
          }
        }));
        announce('Game over. Your settlement has fallen.');
        return;
      }

      const allWorkers = game.tempWorker ? [...aliveWorkers, game.tempWorker] : aliveWorkers;

      const defaultAssignments = {};
      allWorkers.forEach(w => {
        const lastAssignment = game.lastAssignments?.[w.id];
        const smartDefault = w.status === 'injured' ? 'rest' : 'farm';
        defaultAssignments[w.id] = lastAssignment || smartDefault;
      });

      setGame(g => ({ ...g, assignments: defaultAssignments }));
    }
  }, [game.phase, game.assignments, game.workers, game.tempWorker, game.lastAssignments, setGame, announce]);

  // Event phase handlers
  const handleContinueFromEvent = useCallback(() => {
    const event = game.currentEvent;
    const stateWithEffects = applyEventEffect(event, game);

    let finalWorkers = stateWithEffects.workers;
    if (game.secondaryEvents && game.secondaryEvents.length > 0) {
      game.secondaryEvents.forEach(se => {
        if (se.type === 'birth') {
          finalWorkers = [...finalWorkers, se.worker];
        }
      });
    }
    const aliveWorkers = finalWorkers.filter(w => w.status !== 'dead');
    const isGameOver = aliveWorkers.length === 0;

    // Festival: skip assign/results — go straight to end-of-day (no work today)
    if (event.type === 'festival') {
      setGame(g => {
        let updates = { ...stateWithEffects };
        if (g.secondaryEvents?.length > 0) {
          g.secondaryEvents.forEach(se => {
            if (se.type === 'birth') {
              updates.workers = [...updates.workers, se.worker];
              updates.morale = Math.min(100, (updates.morale || 50) + se.moraleBonus);
            }
          });
          updates.secondaryEvents = [];
        }
        return updates;
      });
      handleEndDay();
      announce('Festival! Your people celebrate — no work today.');
      return;
    }

    setGame(g => {
      let updates = { ...stateWithEffects };

      if (g.secondaryEvents && g.secondaryEvents.length > 0) {
        g.secondaryEvents.forEach(se => {
          if (se.type === 'birth') {
            updates.workers = [...updates.workers, se.worker];
            updates.morale = Math.min(100, (updates.morale || 50) + se.moraleBonus);
          }
        });
        updates.secondaryEvents = [];
      }

      const aliveWorkers = updates.workers.filter(w => w.status !== 'dead');
      if (aliveWorkers.length === 0) {
        updates.phase = 'gameover';
        updates.gameOver = true;
        updates.gameWon = false;
        updates.ending = {
          type: 'defeat',
          icon: '💀',
          title: 'The Settlement Falls',
          message: `Your settlement fell on day ${updates.day}. The wilderness claims another dream...`,
          color: 'text-red-700'
        };
        return updates;
      }

      updates.phase = 'assign';
      return updates;
    });

    if (isGameOver) {
      announce('Game over. Your settlement has fallen.');
    } else if (event.type === 'raid') {
      announce('Raiders are attacking! Assign workers to guard.');
    } else if (event.type === 'warning') {
      announce('Raid warning! Attack coming tomorrow.');
    } else {
      announce('Assign your workers to tasks.');
    }
  }, [game, setGame, announce, handleEndDay]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStoryChoice = useCallback((choiceId) => {
    // Handle victory claim prompt specially
    if (game.currentEvent?.type === 'victory_claim') {
      if (choiceId === 'claim') {
        setGame(g => ({
          ...g,
          phase: 'gameover',
          gameWon: true,
          gameOver: false,
          ending: g.pendingVictory,
          pendingVictory: null,
          currentEvent: null
        }));
        audio.playSound('music_victory');
      } else {
        // Keep playing — clear pendingVictory (re-evaluated each end-of-day)
        setGame(g => ({
          ...g,
          phase: 'assign',
          pendingVictory: null,
          currentEvent: null
        }));
        announce('You press on toward even greater glory.');
      }
      return;
    }

    const choice = game.currentEvent.choices.find(c => c.id === choiceId);
    if (!choice) return;

    if (choice.cost) {
      const canAfford =
        (!choice.cost.food || game.food >= choice.cost.food) &&
        (!choice.cost.wood || game.wood >= choice.cost.wood) &&
        (!choice.cost.stone || game.stone >= choice.cost.stone) &&
        (!choice.cost.metal || (game.metal || 0) >= choice.cost.metal);

      if (!canAfford) {
        announce('Not enough resources for this choice!');
        audio.playSound('ui_error');
        return;
      }
    }

    setGame(g => {
      let updates = { ...g };

      if (choice.cost) {
        if (choice.cost.food) updates.food = g.food - choice.cost.food;
        if (choice.cost.wood) updates.wood = g.wood - choice.cost.wood;
        if (choice.cost.stone) updates.stone = g.stone - choice.cost.stone;
        if (choice.cost.metal) updates.metal = (g.metal || 0) - choice.cost.metal;
      }

      if (choice.effects) {
        if (choice.effects.food !== undefined) {
          updates.food = (updates.food ?? g.food) + choice.effects.food;
        }
        if (choice.effects.knowledge !== undefined) {
          updates.knowledge = (g.knowledge || 0) + choice.effects.knowledge;
        }
        if (choice.effects.metal !== undefined) {
          updates.metal = (g.metal || 0) + choice.effects.metal;
        }
        if (choice.effects.wood !== undefined) {
          updates.wood = (updates.wood ?? g.wood) + choice.effects.wood;
        }
        if (choice.effects.stone !== undefined) {
          updates.stone = (updates.stone ?? g.stone) + choice.effects.stone;
        }
        if (choice.effects.morale !== undefined) {
          if (typeof choice.effects.morale === 'number' && choice.effects.morale > 50) {
            updates.morale = choice.effects.morale;
          } else {
            updates.morale = (g.morale || 50) + choice.effects.morale;
          }
        }

        if (choice.effects.futureRefugeesDay) {
          updates.futureRefugees = {
            day: choice.effects.futureRefugeesDay,
            count: choice.effects.futureRefugeesCount
          };
        }

        if (choice.effects.workers) {
          const newWorkers = addRefugees(g.workers, choice.effects.workers, g.day);
          updates.workers = newWorkers;
        }

        if (choice.effects.metalRoll) {
          updates.metal = (g.metal || 0) + rollD6();
        }

        // Refugees roll (d6, max 3) for the refugees event
        if (choice.effects.refugeesRoll) {
          const count = Math.min(rollD6(), 3);
          updates.workers = addRefugees(updates.workers || g.workers, count, g.day);
        }

        if (choice.effects.wallProgress) {
          const progressAmount = choice.effects.wallProgress;
          const wall = g.buildings.find(b => b.type === 'wall');
          const wallThreshold = BUILDINGS.wall?.buildThreshold || CONFIG.BUILD_THRESHOLD;

          if (!wall) {
            // Start wall for free at partial progress
            const newWall = {
              type: 'wall',
              progress: progressAmount,
              complete: progressAmount >= wallThreshold
            };
            updates.buildings = [...g.buildings, newWall];
            if (!newWall.complete) {
              updates.buildingInProgress = { type: 'wall', progress: progressAmount };
            }
          } else if (!wall.complete) {
            const newProgress = wall.progress + progressAmount;
            const isComplete = newProgress >= wallThreshold;
            updates.buildings = g.buildings.map(b =>
              b.type === 'wall'
                ? { ...b, progress: newProgress, complete: isComplete }
                : b
            );
            if (isComplete) {
              updates.buildingInProgress = null;
            } else {
              updates.buildingInProgress = { type: 'wall', progress: newProgress };
            }
          }
        }

        if (choice.effects.farmBonusDays) {
          updates.farmBonus = {
            endsOn: g.day + choice.effects.farmBonusDays,
            amount: choice.effects.farmBonusAmount || 5
          };
        }

        // Heal all injured workers (divine_favor healing choice)
        if (choice.effects.healAll) {
          updates.workers = (updates.workers || g.workers).map(w =>
            w.status === 'injured' ? { ...w, status: 'healthy' } : w
          );
        }
      }

      if (choice.effects.recruitTraveler) {
        const maxId = Math.max(...g.workers.map(w => typeof w.id === 'number' ? w.id : 0));
        const newWorker = createWorker(maxId + 1, g.day);
        updates.workers = [...(updates.workers || g.workers), newWorker];
        updates.tempWorker = null;
      }

      if (choice.effects.tempWorker === true) {
        updates.tempWorker = { id: 'temp', status: 'healthy' };
      } else if (choice.effects.tempWorker === false || choice.effects.recruitTraveler) {
        updates.tempWorker = null;
      }

      if (choice.effects.immatureWorkers) {
        const count = choice.effects.immatureWorkers;
        const hasNursery = g.buildings.some(b => b.type === 'nursery' && b.complete);
        const maturityDays = hasNursery ? 3 : 5;
        const newWorkers = [...(updates.workers || g.workers)];
        for (let i = 0; i < count; i++) {
          const maxId = Math.max(...newWorkers.map(w => typeof w.id === 'number' ? w.id : 0));
          newWorkers.push(createImmatureWorker(maxId + 1, g.day, maturityDays));
        }
        updates.workers = newWorkers;
      }

      if (choice.effects.buildingBonus) {
        updates.buildingCostReduction = 0.2;
      }

      updates.choicesMade = { ...g.choicesMade, [g.day]: choiceId };
      updates.phase = 'assign';

      return updates;
    });

    announce(choice.effects?.message || 'Choice made.');
  }, [game, setGame, announce, audio]);

  // Assignment phase handlers
  const handleAssign = useCallback((workerId, task) => {
    if (hasSpecialMechanic(task, 'requires_selection') && !game.buildingInProgress) {
      assignWorker(workerId, task);
      setShowBuildSelect(true);
      return;
    }
    assignWorker(workerId, task);
  }, [game.buildingInProgress, assignWorker]);

  const handleSelectBuilding = useCallback((type) => {
    const result = startBuilding(type, {
      food: game.food,
      wood: game.wood,
      stone: game.stone,
      metal: game.metal || 0
    }, game.buildings, game.buildingCostReduction || 0);

    if (result) {
      setGame(g => ({
        ...g,
        food: result.resources.food,
        wood: result.resources.wood,
        stone: result.resources.stone,
        metal: result.resources.metal || 0,
        buildingInProgress: result.buildingInProgress,
        buildings: [...g.buildings, result.building],
        buildingCostReduction: null // Consume the discount
      }));

      setShowBuildSelect(false);
      announce(`Started building.`);
      audio.playSound('task_build');
    }
  }, [game, setGame, announce, audio]);

  const handleRoll = useCallback(() => {
    const rolls = rollAllTasks(game.workers, game.assignments, game.tempWorker, game.morale || 50);

    const hasTrade = rolls.trade && rolls.trade.rolls.length > 0;

    if (hasTrade) {
      const tradeTotal = rolls.trade.rolls.reduce((a, b) => a + b, 0);
      setTradeDialogData({ diceTotal: tradeTotal, rolls });
      setShowTradeDialog(true);
      setGame(g => ({ ...g, rolls }));
    } else {
      setGame(g => ({ ...g, phase: 'results', rolls }));
      announce('Dice rolled! Review results and decide whether to push.');
    }
  }, [game.workers, game.assignments, game.tempWorker, game.morale, setGame, announce]);

  // Results phase handlers
  const handlePush = useCallback((task) => {
    const result = pushTask(task, game.buildings);

    if (result.success) {
      audio.playSound('ui_positive');
    } else {
      audio.playSound('ui_negative');
    }

    // Pre-compute whether a kill will happen (based on current state)
    let wasKilled = false;
    if (!result.success) {
      const workerIds = game.rolls[task]?.workers || [];
      const taskWorkers = game.workers.filter(w => workerIds.includes(w.id) && w.id !== 'temp');
      const healthyOnTask = taskWorkers.filter(w => w.status === 'healthy');
      wasKilled = healthyOnTask.length === 0;
    }

    setGame(g => {
      const newPushes = { ...g.pushes, [task]: result };
      let newWorkers = g.workers;

      if (!result.success) {
        const workerIds = g.rolls[task]?.workers || [];
        const taskWorkers = g.workers.filter(w => workerIds.includes(w.id) && w.id !== 'temp');
        const healthyOnTask = taskWorkers.filter(w => w.status === 'healthy');

        if (healthyOnTask.length > 0) {
          // Injure a random healthy worker
          const target = healthyOnTask[Math.floor(Math.random() * healthyOnTask.length)];
          newWorkers = injureWorker(g.workers, target.id, false);
        } else {
          // No healthy workers — kill a random injured one
          const injuredOnTask = taskWorkers.filter(w => w.status === 'injured');
          if (injuredOnTask.length > 0) {
            const target = injuredOnTask[Math.floor(Math.random() * injuredOnTask.length)];
            newWorkers = injureWorker(g.workers, target.id, true);
          }
        }
      }

      return { ...g, pushes: newPushes, workers: newWorkers };
    });

    announce(result.success
      ? `Push successful! Added ${result.value} to ${task}.`
      : wasKilled
        ? `Push failed with ${result.value}! Worker was killed!`
        : `Push failed with ${result.value}! Worker was injured.`
    );
  }, [game.buildings, game.rolls, game.workers, setGame, announce, audio]);

  const handleSelectBounty = useCallback((task) => {
    setBountyTask(task);
    announce(`Bounty applied to ${task}! Output doubled.`);
  }, [setBountyTask, announce]);

  // Combat phase handlers
  const handleContinueFromCombat = useCallback(() => {
    const remainingWorkers = game.workers.filter(w => w.status !== 'dead');
    const gameOver = checkDefeat(remainingWorkers);
    const gameWon = checkVictory(game.day, remainingWorkers);

    setGame(g => ({
      ...g,
      phase: (gameOver || gameWon) ? 'gameover' : 'event',
      day: (gameOver || gameWon) ? g.day : g.day + 1,
      combatResult: null,
      currentEvent: null,
      gameOver,
      gameWon
    }));
  }, [game, setGame]);

  // Trade dialog handlers
  const handleConfirmTrade = useCallback((tradeResult) => {
    setGame(g => {
      const updatedResources = executeTrade(tradeResult, {
        food: g.food,
        wood: g.wood,
        stone: g.stone,
        metal: g.metal || 0
      });
      return {
        ...g,
        food: updatedResources.food,
        wood: updatedResources.wood,
        stone: updatedResources.stone,
        metal: updatedResources.metal,
        phase: 'results'
      };
    });

    setShowTradeDialog(false);
    setTradeDialogData(null);
    announce(`Traded ${tradeResult.cost} ${tradeResult.fromResource} for ${tradeResult.gained} ${tradeResult.toResource}`);
  }, [setGame, announce]);

  const handleCancelTrade = useCallback(() => {
    setShowTradeDialog(false);
    setTradeDialogData(null);
    setGame(g => ({ ...g, phase: 'results' }));
  }, [setGame]);

  return {
    showBuildSelect,
    setShowBuildSelect,
    showTradeDialog,
    tradeDialogData,
    eventHandlers: {
      event: game.currentEvent,
      eventRoll: game.eventRoll,
      secondaryEvents: game.secondaryEvents,
      onContinue: handleContinueFromEvent,
      onStoryChoice: handleStoryChoice
    },
    assignHandlers: {
      workers: game.workers,
      assignments: game.assignments,
      tempWorker: game.tempWorker,
      buildings: game.buildings,
      buildingInProgress: game.buildingInProgress,
      onAssign: handleAssign,
      onRoll: handleRoll
    },
    resultsHandlers: {
      rolls: game.rolls,
      pushes: game.pushes,
      event: game.currentEvent,
      buildings: game.buildings,
      bountyTask: game.bountyTask,
      onPush: handlePush,
      onSelectBounty: handleSelectBounty,
      onEndDay: handleEndDay
    },
    combatHandlers: {
      result: game.combatResult,
      onContinue: handleContinueFromCombat
    },
    buildHandlers: {
      buildings: game.buildings,
      wood: game.wood,
      stone: game.stone,
      metal: game.metal,
      day: game.day,
      techPoints: game.techPoints,
      knowledge: game.knowledge,
      morale: game.morale,
      workers: game.workers,
      raidsDefeated: game.raidsDefeated,
      buildingCostReduction: game.buildingCostReduction,
      onSelect: handleSelectBuilding,
      onCancel: () => setShowBuildSelect(false)
    },
    tradeHandlers: {
      data: tradeDialogData,
      currentResources: {
        food: game.food,
        wood: game.wood,
        stone: game.stone,
        metal: game.metal || 0
      },
      onConfirmTrade: handleConfirmTrade,
      onCancel: handleCancelTrade
    },
    gameOverHandlers: {
      won: game.gameWon,
      day: game.day,
      ending: game.ending,
      onRestart: reset
    }
  };
}
